/**
 * A custom directive that allows Alpine components to subscribe to Shopify theme editor events.
 * `x-on-shopify` requires a modifier and a value.
 *
 * Example: x-on-shopify:block-select="onShopifyBlockSelect"
 *
 * Modifier:
 * The modifier should be the name of the Shopify event you want to subscribe to in kebab case
 * e.g. shopify:section:select => x-on-shopify:section-select
 * For a list of all events see:
 * https://shopify.dev/docs/themes/architecture/sections/integrate-sections-with-the-theme-editor#javascript-events
 *
 * Value:
 * The value should be the name of the callback function you want to run when the event is emitted.
 * This should be a method on your Alpine component.
 *
 *
 * IMPORTANT:
 * Ensure you add `data-section-id` and `data-block-id` attributes to your elements where appropriate.
 * These are needed to scope events to specific section instances.
 *
 * You should also add {{ block.shopify_attributes }} to any blocks used. This is required by Shopify's
 * theme editor to correctly identify the target when emitting block events.
 *
 * e.g:
 *  <div data-section-id="{{ section.id }}">
 *    ...
 *    {% for block in section.blocks %}
 *      <div data-block-id="{{ block.id }}" {{ block.shopify_attributes }}>
 *        ...
 *      </div>
 *    {% endfor %}
 *  </div>
 */

document.addEventListener('alpine:init', () => {
  const eventNames = [
    'shopify:inspector:activate',
    'shopify:inspector:deactivate',
    'shopify:section:load',
    'shopify:section:unload',
    'shopify:section:select',
    'shopify:section:deselect',
    'shopify:section:reorder',
    'shopify:block:select',
    'shopify:block:deselect',
  ];

  const shopifyThemeEvents = {
    events: {},

    subscribe(eventName, fn) {
      this.events[eventName] = this.events[eventName] || [];
      this.events[eventName].push(fn);
    },

    unsubscribe(eventName, fn) {
      if (this.events[eventName]) {
        for (var i = 0; i < this.events[eventName].length; i++) {
          if (this.events[eventName][i] === fn) {
            this.events[eventName].splice(i, 1);
            break;
          }
        }
      }
    },

    publish(eventName, payload) {
      if (this.events[eventName]) {
        this.events[eventName].forEach(function (fn) {
          fn(payload);
        });
      }
    },

    setEventListeners() {
      eventNames.forEach((eventName) => {
        document.addEventListener(eventName, (event) => this.emitEvent(event));
      });
    },

    emitEvent(event) {
      const { sectionId } = event.detail;
      const normalizedEventName = convertToCamelCase(event.type);
      const container = document.querySelector(`[data-section-id="${sectionId}"]`);

      if (container !== null) {
        const payload = {
          event,
          container,
        };

        setTimeout(() => {
          this.publish(`${normalizedEventName}:${sectionId}`, payload);
        }, 0);
      }
    },

    init() {
      if (window.Shopify.designMode) {
        this.setEventListeners();
      }
    },
  };

  function setupAlpineIntegration(Alpine) {
    shopifyThemeEvents.init();

    Alpine.directive('on-shopify', (el, { value, expression }, { evaluate, cleanup }) => {
      if (!window.Shopify.designMode) {
        return;
      }

      const sectionId = el?.dataset?.sectionId;
      const normalizedEventName = convertToCamelCase(`shopify-${value}`);
      const scopedEventName = `${normalizedEventName}:${sectionId}`;
      const callback = (...args) => evaluate(expression, { params: [...args] });

      if (sectionId) {
        shopifyThemeEvents.subscribe(scopedEventName, callback);

        cleanup(() => {
          shopifyThemeEvents.unsubscribe(scopedEventName, callback);
        });
      }
    });
  }
  setupAlpineIntegration(window.Alpine);
});
